import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Heart, Users, TrendingUp, TrendingDown, Minus, ArrowRight, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useRelationshipAssessment } from '@/hooks/useRelationshipAssessment';
import { usePartnerLink } from '@/hooks/usePartnerLink';
import { getScoreInterpretation } from '@/data/relationshipAssessmentQuestions';

export function RelationalInsightsCard() {
  const [relationalFeeling, setRelationalFeeling] = useState<string | null>(null);
  const [recentTrend, setRecentTrend] = useState<'up' | 'down' | 'stable' | null>(null);
  const { assessments, getLatestAssessment } = useRelationshipAssessment();
  const { isLinked, partnerLink } = usePartnerLink();

  const latestAssessment = getLatestAssessment();
  const previousAssessment = assessments.length > 1 ? assessments[1] : null;

  useEffect(() => {
    const fetchRelationalData = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Get recent check-ins for relational feeling
        const { data: checkins } = await supabase
          .from('daily_checkins')
          .select('relational_feeling, date')
          .eq('user_id', user.id)
          .order('date', { ascending: false })
          .limit(7);

        if (checkins && checkins.length > 0) {
          const latestWithFeeling = checkins.find(c => c.relational_feeling);
          if (latestWithFeeling) {
            setRelationalFeeling(latestWithFeeling.relational_feeling);
          }

          // Calculate simple trend from feelings
          const recentFeelings = checkins.filter(c => c.relational_feeling).slice(0, 5);
          if (recentFeelings.length >= 2) {
            const positiveWords = ['Connected', 'Loved', 'Supported', 'Grateful', 'Intimate'];
            const negativeWords = ['Disconnected', 'Lonely', 'Frustrated', 'Distant', 'Overwhelmed'];
            
            const recent = recentFeelings.slice(0, 2);
            const older = recentFeelings.slice(2, 4);
            
            const recentPositive = recent.filter(r => 
              positiveWords.some(w => r.relational_feeling?.includes(w))
            ).length;
            const olderPositive = older.filter(r => 
              positiveWords.some(w => r.relational_feeling?.includes(w))
            ).length;
            
            if (recentPositive > olderPositive) setRecentTrend('up');
            else if (recentPositive < olderPositive) setRecentTrend('down');
            else setRecentTrend('stable');
          }
        }
      } catch (error) {
        console.error('Error fetching relational data:', error);
      }
    };

    fetchRelationalData();
  }, []);

  const healthScore = latestAssessment?.relationship_health_score || 0;
  const scoreDiff = previousAssessment 
    ? healthScore - (previousAssessment.relationship_health_score || 0)
    : null;

  const TrendIcon = recentTrend === 'up' ? TrendingUp : recentTrend === 'down' ? TrendingDown : Minus;
  const trendColor = recentTrend === 'up' ? 'text-accent' : recentTrend === 'down' ? 'text-secondary' : 'text-muted-foreground';

  return (
    <Card className="bg-gradient-to-br from-secondary/5 via-primary/5 to-background border-secondary/10 hover:shadow-md transition-shadow">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-secondary/10">
              <Heart className="w-5 h-5 text-secondary" />
            </div>
            <div>
              <CardTitle className="text-base">Relational Wellbeing</CardTitle>
              <CardDescription className="text-xs">Your connection health</CardDescription>
            </div>
          </div>
          {isLinked && (
            <Badge variant="secondary" className="text-xs">
              <Users className="w-3 h-3 mr-1" />
              Linked
            </Badge>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Current State */}
        <div className="grid grid-cols-2 gap-3">
          {/* Today's Feeling */}
          <div className="p-3 bg-muted/50 rounded-lg">
            <p className="text-xs text-muted-foreground mb-1">Feeling Today</p>
            {relationalFeeling ? (
              <div className="flex items-center gap-1">
                <p className="text-sm font-medium truncate">{relationalFeeling}</p>
                {recentTrend && (
                  <TrendIcon className={`w-3 h-3 ${trendColor}`} />
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Not tracked yet</p>
            )}
          </div>

          {/* Assessment Score */}
          <div className="p-3 bg-muted/50 rounded-lg">
            <p className="text-xs text-muted-foreground mb-1">Health Score</p>
            {latestAssessment ? (
              <div className="flex items-center gap-1">
                <p 
                  className="text-sm font-bold"
                  style={{ 
                    color: getScoreInterpretation(Math.round(healthScore / 100 * 21)).color 
                  }}
                >
                  {Math.round(healthScore)}%
                </p>
                {scoreDiff !== null && Math.abs(scoreDiff) >= 2 && (
                  <span className={`text-xs ${scoreDiff > 0 ? 'text-green-500' : 'text-orange-500'}`}>
                    {scoreDiff > 0 ? '+' : ''}{Math.round(scoreDiff)}
                  </span>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No assessment</p>
            )}
          </div>
        </div>

        {/* Quick Insight */}
        {latestAssessment && (
          <div className="p-3 bg-gradient-to-r from-pink-500/10 to-transparent rounded-lg">
            <div className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-pink-500 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-pink-700 dark:text-pink-300">
                  {getScoreInterpretation(Math.round(healthScore / 100 * 21)).label}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {getScoreInterpretation(Math.round(healthScore / 100 * 21)).description}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Partner Status */}
        {isLinked && partnerLink && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Users className="w-3 h-3" />
            <span>Connected with {partnerLink.partner_display_name}</span>
          </div>
        )}

        {/* Action Button */}
        <Link to="/sanctuary?tab=relational">
          <Button variant="outline" size="sm" className="w-full">
            Explore Relational Patterns
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}
